import Quill from 'quill';
import React from 'react';

import 'quill/dist/quill.snow.css';

export type EditorHandle = {
  insertText: (text: string) => void;
  setContent: (html: string) => void;
};

type EditorProps = {
  value: string;
  onChange: (html: string) => void;
  onReady?: (handle: EditorHandle) => void;
  disabled?: boolean;
};

const TOOLBAR_OPTIONS = [
  [{ size: ['small', false, 'large'] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['clean'],
];

const Editor = (props: EditorProps) => {
  const { value, onChange, onReady, disabled = false } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const quillRef = React.useRef<Quill | null>(null);
  const onChangeRef = React.useRef(onChange);
  const onReadyRef = React.useRef(onReady);

  onChangeRef.current = onChange;
  onReadyRef.current = onReady;

  React.useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    const quill = new Quill(containerRef.current, {
      theme: 'snow',
      modules: {
        toolbar: TOOLBAR_OPTIONS,
      },
    });

    quill.root.innerHTML = value;
    quillRef.current = quill;

    let lastSelection = 0;

    quill.on('text-change', () => {
      onChangeRef.current(quill.root.innerHTML);
    });

    quill.on('selection-change', (range) => {
      if (range) {
        lastSelection = range.index;
      }
    });

    if (onReadyRef.current) {
      onReadyRef.current({
        insertText: (text: string) => {
          quill.focus();
          quill.insertText(lastSelection, text);
          quill.setSelection(lastSelection + text.length, 0);
          lastSelection += text.length;
        },
        setContent: (html: string) => {
          quill.root.innerHTML = html;
          lastSelection = 0;
        },
      });
    }
  }, []);

  React.useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!disabled);
    }
  }, [disabled]);

  return (
    <div className="quill-wrapper">
      <div ref={containerRef} />
    </div>
  );
};

export default Editor;
